import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';
import {
  CLIENT_SLOTCARS_INTERPOLATION_MS,
  buildSmoothClosedPath,
  buildTrackMetrics,
  findCornerForProgress,
  getRacingLineOffset,
  interpolateWrappedProgress,
  normalizeProgress,
  sampleTrackPosition,
} from '../../utils/slotCars';

const FALLBACK_COLORS = ['#f97316', '#38bdf8', '#f43f5e', '#a3e635', '#facc15', '#c084fc', '#fb7185', '#2dd4bf'];
const SKID_MARK_FADE_MS = 2000;
const CRASH_ENTRY_MS = 900;
const START_GO_FLASH_MS = 900;

function easeOutCubic(value) {
  const clamped = Math.max(0, Math.min(1, value));
  return 1 - ((1 - clamped) ** 3);
}

function smoothstep(edge0, edge1, value) {
  if (edge0 === edge1) return value >= edge1 ? 1 : 0;
  const ratio = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return ratio * ratio * (3 - (2 * ratio));
}

function buildLinePath(points) {
  if (!Array.isArray(points) || points.length === 0) return '';
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

function buildSkidMarkPaths(metrics, track, mark, slotOffset = 0) {
  if (!metrics?.totalLength || !track) return { left: '', right: '' };
  const trailLength = 0.018 + ((mark.severity || 0.4) * 0.024);
  const leadProgress = 0.006 + ((mark.severity || 0.4) * 0.016);
  const driftStart = (mark.direction || 1) * (track.laneWidth || 40) * 0.16;
  const driftEnd = (mark.direction || 1) * (track.laneWidth || 40) * (0.56 + ((mark.severity || 0.4) * 0.28));
  const wheelSpacing = 4.5;
  const buildPoints = (wheelOffset) => Array.from({ length: 7 }, (_, index) => {
    const ratio = index / 6;
    const progress = normalizeProgress(mark.progress + (leadProgress * ratio) - (trailLength * (1 - ratio)));
    const drift = driftStart + ((driftEnd - driftStart) * ratio);
    return sampleTrackPosition(metrics, progress, slotOffset + drift + wheelOffset);
  });

  return {
    left: buildLinePath(buildPoints(-wheelSpacing)),
    right: buildLinePath(buildPoints(wheelSpacing)),
  };
}

function getCrashPose(metrics, track, progress, baseOffset, car) {
  if (!car?.crashed) {
    return sampleTrackPosition(metrics, progress || 0, baseOffset);
  }
  const direction = car.crashDirection || car.slipDirection || 1;
  const severity = Math.max(0.25, car.crashSeverity || car.slipSeverity || 0.35);
  const crashRatio = Math.max(0, Math.min(1, car.crashVisualRatio ?? 1));
  const forwardRatio = easeOutCubic(smoothstep(0, 0.7, crashRatio));
  const spinRatio = easeOutCubic(smoothstep(0.12, 0.68, crashRatio));
  const peakSpinAngle = direction * (145 + (severity * 105)) * spinRatio;
  const spinAngle = peakSpinAngle;
  const forwardProgress = (0.024 + (severity * 0.08)) * forwardRatio;
  const pose = sampleTrackPosition(metrics, normalizeProgress((progress || 0) + forwardProgress), baseOffset);
  return {
    ...pose,
    angle: pose.angle + spinAngle,
  };
}

function getPlayerColor(player, index) {
  return player?.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function formatSpeed(speed, maxSpeed) {
  if (!Number.isFinite(speed) || !Number.isFinite(maxSpeed) || maxSpeed <= 0) return '0%';
  return `${Math.round((speed / maxSpeed) * 100)}%`;
}

function formatLapTime(timeMs) {
  if (!Number.isFinite(timeMs) || timeMs <= 0) return '--:--.--';
  const totalCentiseconds = Math.round(timeMs / 10);
  const minutes = Math.floor(totalCentiseconds / 6000);
  const seconds = Math.floor((totalCentiseconds % 6000) / 100);
  const centiseconds = totalCentiseconds % 100;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
}

function formatOrdinal(value) {
  const number = Number(value) || 0;
  const mod100 = number % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${number}th`;
  switch (number % 10) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
}

function SlotCarsGame({ gameState, gamePlayers, user, onPlayAgain, socket, roomId, isSpectator }) {
  const { submitAction, normalizedState, findPlayerById } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = useMemo(() => normalizedState || gameState || {}, [normalizedState, gameState]);
  const track = state.track || {};
  const players = useMemo(() => (
    Array.isArray(state.players) ? state.players : (Array.isArray(gamePlayers) ? gamePlayers : [])
  ), [gamePlayers, state.players]);
  const meId = user?.socketId || user?.userId || null;
  const localCar = meId ? state.cars?.[meId] : null;
  const [isHoldingThrottle, setIsHoldingThrottle] = useState(false);
  const holdingRef = useRef(false);
  const previousCarsRef = useRef(state.cars || {});
  const interpolationRef = useRef({
    previousCars: state.cars || {},
    targetCars: state.cars || {},
    startedAt: Date.now(),
  });
  const [skidMarks, setSkidMarks] = useState([]);

  const metrics = useMemo(() => buildTrackMetrics(track.path || []), [track.path]);
  const roadPath = useMemo(() => buildSmoothClosedPath(metrics.points || []), [metrics.points]);
  const renderedRoadWidth = Math.max(14, (track.laneWidth || 40) - 4);
  const renderedRoadEdgeWidth = renderedRoadWidth + 8;
  const [animationNow, setAnimationNow] = useState(() => Date.now());

  useEffect(() => {
    interpolationRef.current = {
      previousCars: displayCars || state.cars || {},
      targetCars: state.cars || {},
      startedAt: Date.now(),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cars]);

  useEffect(() => {
    const previousCars = previousCarsRef.current || {};
    const now = Date.now();
    const nextMarks = [];

    Object.entries(state.cars || {}).forEach(([playerId, car]) => {
      const previousCar = previousCars[playerId];
      if (car?.crashed && !previousCar?.crashed) {
        nextMarks.push({
          id: `${playerId}-${car.crashedAt || now}`,
          playerId,
          progress: typeof car.crashProgress === 'number' ? car.crashProgress : (car.progress || 0),
          direction: car.crashDirection || previousCar?.slipDirection || 1,
          severity: car.crashSeverity || previousCar?.slipSeverity || 0.4,
          createdAt: car.crashedAt || now,
        });
      }
    });

    previousCarsRef.current = state.cars || {};

    if (nextMarks.length === 0) return;
    setSkidMarks((current) => ([
      ...current.filter((mark) => (now - mark.createdAt) < SKID_MARK_FADE_MS),
      ...nextMarks,
    ].slice(-24)));
  }, [state.cars]);

  useEffect(() => {
    let frameId = null;
    const tick = () => {
      setAnimationNow(Date.now());
      frameId = window.requestAnimationFrame(tick);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  const displayCars = useMemo(() => {
    const { previousCars, targetCars, startedAt } = interpolationRef.current;
    const ratio = Math.max(0, Math.min((animationNow - startedAt) / CLIENT_SLOTCARS_INTERPOLATION_MS, 1));
    const playerIds = Array.from(new Set([
      ...Object.keys(previousCars || {}),
      ...Object.keys(targetCars || {}),
    ]));

    return Object.fromEntries(playerIds.map((playerId) => {
      const previousCar = previousCars?.[playerId] || targetCars?.[playerId] || null;
      const targetCar = targetCars?.[playerId] || previousCars?.[playerId] || null;
      if (!previousCar || !targetCar) return [playerId, targetCar || previousCar];
      if (targetCar.finishedAt || previousCar.finishedAt) return [playerId, targetCar];

      if (targetCar.crashed && !previousCar.crashed) {
        const crashStartedAt = targetCar.crashedAt || startedAt;
        const crashRatio = Math.max(0, Math.min(1, (animationNow - crashStartedAt) / CRASH_ENTRY_MS));
        return [playerId, {
          ...targetCar,
          progress: interpolateWrappedProgress(previousCar.progress || 0, targetCar.progress || 0, Math.min(ratio, 0.96)),
          speed: (previousCar.speed || 0) * (1 - easeOutCubic(Math.min(1, crashRatio * 1.15))),
          crashVisualRatio: crashRatio,
        }];
      }

      if (targetCar.crashed) {
        const crashStartedAt = targetCar.crashedAt || startedAt;
        const crashRatio = Math.max(0, Math.min(1, (animationNow - crashStartedAt) / CRASH_ENTRY_MS));
        return [playerId, { ...targetCar, crashVisualRatio: crashRatio }];
      }

      if (previousCar.crashed) {
        return [playerId, targetCar];
      }

      return [playerId, {
        ...targetCar,
        progress: interpolateWrappedProgress(previousCar.progress || 0, targetCar.progress || 0, ratio),
        speed: (previousCar.speed || 0) + (((targetCar.speed || 0) - (previousCar.speed || 0)) * ratio),
      }];
    }));
  }, [animationNow]);

  const standings = useMemo(() => {
    const ranked = Array.isArray(state.standings) ? state.standings : [];
    if (ranked.length > 0) return ranked;
    return players.map((player) => {
      const playerId = player.socketId || player.userId || player.id;
      const car = displayCars?.[playerId] || state.cars?.[playerId] || {};
      return {
        playerId,
        lap: car.lap || 0,
        progress: car.progress || 0,
        speed: car.speed || 0,
        lastLapTimeMs: car.lastLapTimeMs ?? null,
        bestLapTimeMs: car.bestLapTimeMs ?? null,
        crashed: !!car.crashed,
        slipping: !!car.slipping,
        crashCount: car.crashCount || 0,
      };
    });
  }, [displayCars, players, state.cars, state.standings]);

  const visibleSkidMarks = useMemo(() => (
    skidMarks.filter((mark) => (animationNow - mark.createdAt) < SKID_MARK_FADE_MS)
  ), [animationNow, skidMarks]);

  const displayLocalCar = meId ? (displayCars?.[meId] || localCar) : localCar;
  const localPositionLabel = useMemo(() => {
    if (!meId) return '--';
    const index = standings.findIndex((entry) => entry.playerId === meId);
    return index >= 0 ? formatOrdinal(index + 1) : '--';
  }, [meId, standings]);
  const fastestLapSummary = useMemo(() => {
    const fastestLap = state.fastestLap;
    if (!fastestLap?.playerId || !Number.isFinite(fastestLap?.timeMs)) return null;
    const player = findPlayerById(fastestLap.playerId);
    return {
      playerName: player?.username || fastestLap.playerId,
      timeLabel: formatLapTime(fastestLap.timeMs),
      lap: fastestLap.lap,
    };
  }, [findPlayerById, state.fastestLap]);
  const activeCorner = useMemo(() => {
    if (!localCar) return null;
    const displayCar = meId ? displayCars?.[meId] : null;
    return findCornerForProgress(track.corners, displayCar?.progress || localCar?.progress || 0);
  }, [displayCars, localCar, meId, track.corners]);
  const speedGauge = useMemo(() => {
    const maxSpeed = track.maxSpeed || 0.18;
    const currentSpeed = Math.max(0, displayLocalCar?.speed || 0);
    const ratio = !Number.isFinite(maxSpeed) || maxSpeed <= 0 ? 0 : Math.max(0, Math.min(1.25, currentSpeed / maxSpeed));
    const cornerLimit = activeCorner?.maxSpeed || maxSpeed;
    const limitRatio = !Number.isFinite(cornerLimit) || cornerLimit <= 0 ? 1 : Math.max(0, Math.min(1, cornerLimit / maxSpeed));
    const overLimit = ratio > limitRatio;
    const nearLimit = !overLimit && ratio >= (limitRatio * 0.82);

    let fillClass = 'bg-emerald-400';
    if (overLimit) fillClass = 'bg-rose-500';
    else if (nearLimit) fillClass = 'bg-amber-400';

    return {
      ratio,
      limitRatio,
      fillClass,
      overLimit,
      nearLimit,
      speedLabel: `${Math.round(ratio * 100)}%`,
      limitLabel: `${Math.round(limitRatio * 100)}%`,
    };
  }, [activeCorner?.maxSpeed, displayLocalCar?.speed, track.maxSpeed]);

  const countdownLabel = useMemo(() => {
    if (!state.countdownEndsAt || state.finished) return null;
    const remainingMs = state.countdownEndsAt - animationNow;
    if (state.countdownToStart) {
      return String(Math.max(1, Math.ceil(Math.max(0, remainingMs) / 1000)));
    }
    if (remainingMs <= -START_GO_FLASH_MS) return null;
    return 'GO';
  }, [animationNow, state.countdownEndsAt, state.countdownToStart, state.finished]);

  const countdownPalette = useMemo(() => {
    switch (countdownLabel) {
      case '3':
        return {
          fill: 'rgba(220, 38, 38, 0.82)',
          stroke: 'rgba(254, 202, 202, 0.92)',
          text: '#fff7f7',
        };
      case '2':
        return {
          fill: 'rgba(234, 88, 12, 0.82)',
          stroke: 'rgba(254, 215, 170, 0.92)',
          text: '#fff7ed',
        };
      case '1':
        return {
          fill: 'rgba(234, 179, 8, 0.88)',
          stroke: 'rgba(254, 240, 138, 0.96)',
          text: '#422006',
        };
      case 'GO':
        return {
          fill: 'rgba(22, 163, 74, 0.86)',
          stroke: 'rgba(187, 247, 208, 0.96)',
          text: '#f0fdf4',
        };
      default:
        return {
          fill: 'rgba(2,6,23,0.72)',
          stroke: 'rgba(248,250,252,0.22)',
          text: '#f8fafc',
        };
    }
  }, [countdownLabel]);

  const countdownActive = !!state.countdownToStart;

  const startLine = useMemo(() => sampleTrackPosition(metrics, track.startProgress || 0, 0), [metrics, track.startProgress]);
  const startLineRadians = (startLine.angle * Math.PI) / 180;
  const startLineLength = Math.max(18, (track.laneWidth || 40) * 0.9);
  const startLineX1 = startLine.x + (Math.cos(startLineRadians + Math.PI / 2) * startLineLength);
  const startLineY1 = startLine.y + (Math.sin(startLineRadians + Math.PI / 2) * startLineLength);
  const startLineX2 = startLine.x + (Math.cos(startLineRadians - Math.PI / 2) * startLineLength);
  const startLineY2 = startLine.y + (Math.sin(startLineRadians - Math.PI / 2) * startLineLength);

  const endThrottle = React.useCallback(() => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    setIsHoldingThrottle(false);
    if (!state.finished) {
      submitAction({ type: 'accelerateEnd' }).catch(() => {});
    }
  }, [state.finished, submitAction]);

  const startThrottle = React.useCallback((event) => {
    if (isSpectator || state.finished || !localCar || localCar.crashed || holdingRef.current) return;
    holdingRef.current = true;
    setIsHoldingThrottle(true);
    try {
      event?.currentTarget?.setPointerCapture?.(event.pointerId);
    } catch (error) {}
    submitAction({ type: 'accelerateStart' }).catch(() => {
      holdingRef.current = false;
      setIsHoldingThrottle(false);
    });
  }, [isSpectator, localCar, state.finished, submitAction]);

  const trackIsInteractive = !isSpectator && !state.finished && !!localCar && !localCar.crashed;

  useEffect(() => {
    if (state.finished || localCar?.crashed) {
      endThrottle();
    }
  }, [endThrottle, localCar?.crashed, state.finished]);

  useEffect(() => {
    const release = () => endThrottle();
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', release);
      release();
    };
  }, [endThrottle]);

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 p-4 text-slate-100">
      <div className="overflow-hidden rounded-[28px] border border-slate-700/70 bg-[radial-gradient(circle_at_top,#1f2937,#020617_68%)] shadow-[0_24px_60px_rgba(2,6,23,0.55)]">
        <div className="grid grid-cols-[auto,minmax(0,1fr),auto] items-center gap-3 border-b border-slate-800/80 px-3 py-2 text-[11px] text-slate-200">
          <div className="truncate font-semibold text-white">
            {localCar ? `Lap ${Math.min(localCar.lap || 0, track.lapsToWin || state.config?.lapsToWin || 1)}/${track.lapsToWin || state.config?.lapsToWin || 1}` : 'Lap --'}
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Speed</span>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-900/90 ring-1 ring-inset ring-slate-700/80">
              <div
                className="absolute inset-y-0 left-0"
                style={{
                  width: `${Math.round(Math.min(1, speedGauge.limitRatio) * 100)}%`,
                  background: 'linear-gradient(90deg, rgba(34,197,94,0.16) 0%, rgba(34,197,94,0.08) 100%)',
                }}
              />
              <div
                className={`relative h-full rounded-full ${speedGauge.fillClass}`}
                style={{
                  width: `${Math.round(speedGauge.ratio * 100)}%`,
                  boxShadow: speedGauge.overLimit ? '0 0 14px rgba(244,63,94,0.55)' : speedGauge.nearLimit ? '0 0 12px rgba(251,191,36,0.35)' : '0 0 10px rgba(34,197,94,0.28)',
                }}
              />
              <div
                className="pointer-events-none absolute top-0 h-full w-[2px] bg-slate-100/90 shadow-[0_0_8px_rgba(255,255,255,0.55)]"
                style={{
                  left: `${Math.round(Math.min(1, speedGauge.limitRatio) * 100)}%`,
                }}
              />
            </div>
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{speedGauge.speedLabel}</span>
          </div>
          <div className="truncate text-right font-semibold text-white">
            {`Pos ${localPositionLabel}`}
          </div>
        </div>
        <div className="px-3 pb-3 pt-3">
          <div
            className={`overflow-hidden rounded-[26px] border border-slate-700/70 bg-slate-950/70 transition ${trackIsInteractive ? 'cursor-pointer active:scale-[0.997]' : 'cursor-default'}`}
            onPointerDown={startThrottle}
            onPointerUp={endThrottle}
            onPointerCancel={endThrottle}
            onLostPointerCapture={endThrottle}
            onDoubleClick={(event) => event.preventDefault()}
            onContextMenu={(event) => event.preventDefault()}
            onSelectStart={(event) => event.preventDefault()}
            style={{
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
            role="button"
            aria-label="Press and hold anywhere on the track to accelerate"
            tabIndex={trackIsInteractive ? 0 : -1}
          >
            <svg viewBox={`0 0 ${track.width || 320} ${track.height || 560}`} className="block h-auto w-full">
              <rect x="0" y="0" width={track.width || 320} height={track.height || 560} fill={track.palette?.infield || '#143a2d'} />
              <path d={roadPath} fill="none" stroke={track.palette?.roadEdge || '#e5e7eb'} strokeWidth={renderedRoadEdgeWidth} strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
              <path d={roadPath} fill="none" stroke={track.palette?.road || '#475569'} strokeWidth={renderedRoadWidth} strokeLinecap="round" strokeLinejoin="round" />
              <line x1={startLineX1} y1={startLineY1} x2={startLineX2} y2={startLineY2} stroke="#f8fafc" strokeWidth="5" strokeLinecap="round" />
              {countdownLabel ? (
                <g pointerEvents="none">
                  <circle
                    cx={(track.width || 320) / 2}
                    cy={(track.height || 560) / 2}
                    r={38}
                    fill={countdownPalette.fill}
                    stroke={countdownPalette.stroke}
                    strokeWidth="2"
                  />
                  <text
                    x="50%"
                    y="50%"
                    fill={countdownPalette.text}
                    fontSize={countdownLabel === 'GO' ? '26' : '32'}
                    fontWeight="900"
                    letterSpacing="0.08em"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {countdownLabel}
                  </text>
                </g>
              ) : null}
              {visibleSkidMarks.map((mark) => {
                const playerIndex = players.findIndex((player) => (player.socketId || player.userId || player.id) === mark.playerId);
                const slotOffset = playerIndex >= 0 ? ((playerIndex - ((players.length - 1) / 2)) * 8) : 0;
                const paths = buildSkidMarkPaths(metrics, track, mark, slotOffset);
                const opacity = Math.max(0, 1 - ((animationNow - mark.createdAt) / SKID_MARK_FADE_MS));
                return (
                  <g key={mark.id} opacity={opacity * 0.78}>
                    <path d={paths.left} fill="none" stroke="rgba(15,23,42,0.88)" strokeWidth="2.4" strokeLinecap="round" />
                    <path d={paths.right} fill="none" stroke="rgba(15,23,42,0.88)" strokeWidth="2.4" strokeLinecap="round" />
                  </g>
                );
              })}
              {players.map((player, index) => {
                const playerId = player.socketId || player.userId || player.id;
                const car = displayCars?.[playerId] || state.cars?.[playerId];
                if (!car) return null;
                const slotOffset = ((index - ((players.length - 1) / 2)) * 8);
                const racingLineOffset = getRacingLineOffset(metrics, track, car.progress || 0, car);
                const pose = getCrashPose(metrics, track, car.progress || 0, slotOffset + racingLineOffset, car);
                const color = getPlayerColor(player, index);
                const playerName = player.username || playerId;
                return (
                  <g key={playerId} transform={`translate(${pose.x} ${pose.y}) rotate(${pose.angle})`} opacity={car.crashed ? 0.45 : 1}>
                    <ellipse cx="0" cy="0" rx="13" ry="8" fill="rgba(15,23,42,0.45)" />
                    <rect x="-12" y="-7" width="24" height="14" rx="6" fill={color} stroke="rgba(255,255,255,0.72)" strokeWidth="1.6" />
                    <rect x="-5.5" y="-4.5" width="11" height="5" rx="2.5" fill="rgba(226,232,240,0.82)" />
                    <circle cx="-6.5" cy="-7.5" r="2.25" fill="#020617" />
                    <circle cx="6.5" cy="-7.5" r="2.25" fill="#020617" />
                    <circle cx="-6.5" cy="7.5" r="2.25" fill="#020617" />
                    <circle cx="6.5" cy="7.5" r="2.25" fill="#020617" />
                    {car.slipping ? <path d="M -16 5 Q -6 11 2 6 T 18 6" stroke="rgba(248,250,252,0.7)" strokeWidth="1.6" fill="none" strokeLinecap="round" /> : null}
                    {car.crashed ? <path d="M -6 -6 L 6 6 M 6 -6 L -6 6" stroke="#f8fafc" strokeWidth="2" strokeLinecap="round" /> : null}
                    <text x="0" y="-13" fill="#f8fafc" fontSize="8.5" fontWeight="700" textAnchor="middle">{playerName}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {!state.finished ? (
          <div className="px-4 pb-1">
            <div className={`rounded-2xl border px-3 py-3 text-sm ${speedGauge.overLimit ? 'border-rose-400/60 bg-rose-500/10 text-rose-100' : speedGauge.nearLimit ? 'border-amber-400/60 bg-amber-500/10 text-amber-50' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'}`}>
              {activeCorner
                ? `${activeCorner.label}: keep speed under ${formatSpeed(activeCorner.maxSpeed, track.maxSpeed || 0.18)}. Currently ${speedGauge.overLimit ? 'too fast' : speedGauge.nearLimit ? 'close to the limit' : 'in the clear'}.`
                : `Straight: speed is ${speedGauge.overLimit ? 'above' : 'within'} the current limit.`}
            </div>
          </div>
        ) : null}

        <div className="border-t border-slate-800/80 px-4 pb-5 pt-4">
          <div className="mt-3 text-center text-sm text-slate-400">
            {isSpectator
              ? 'Spectating only.'
              : state.finished
                ? `${findPlayerById(state.winner)?.username || 'Winner'} took the chequered flag.`
                : countdownActive
                  ? (isHoldingThrottle
                    ? 'Hold is registered. Wait for GO: staying on the throttle through the lights will cause wheelspin.'
                    : 'Countdown started. Press right on GO for the fastest launch.')
                  : localCar?.launchMode === 'wheelspin'
                    ? 'Wheelspin off the line... feather it for grip.'
                    : localCar?.launchMode === 'boost'
                      ? 'Clean launch. You caught the GO perfectly.'
                : localCar?.slipping
                  ? `Sliding at ${localCar.lastCornerLabel || 'the corner'}... lift and let the car settle.`
                : localCar?.crashed
                  ? `Off the track${localCar.lastCornerLabel ? ` at ${localCar.lastCornerLabel}` : ''}. The marshal is resetting your car.`
                  : isHoldingThrottle
                    ? 'Power is building... keep holding anywhere on the track.'
                    : 'Press and hold anywhere on the track to drive faster.'}
          </div>
        </div>
      </div>

      <div className="rounded-[28px] border border-slate-800/80 bg-slate-950/70 p-4 shadow-[0_18px_40px_rgba(2,6,23,0.35)]">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-slate-500">Standings</div>
            <div className="mt-1 text-lg font-semibold text-white">Current race order</div>
            <div className="mt-1 text-xs text-slate-400">
              {fastestLapSummary
                ? `Fastest lap: ${fastestLapSummary.playerName} ${fastestLapSummary.timeLabel}`
                : 'Fastest lap: --:--.--'}
            </div>
          </div>
          {state.finished && onPlayAgain ? (
            <button type="button" onClick={onPlayAgain} className="rounded-full border border-slate-700 bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-white">
              Play Again
            </button>
          ) : null}
        </div>
        <div className="space-y-2">
          {standings.map((entry, index) => {
            const player = findPlayerById(entry.playerId);
            const color = getPlayerColor(player, index);
            return (
              <div key={entry.playerId} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/70 px-3 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-black text-slate-950" style={{ backgroundColor: color }}>
                    {index + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-white">{player?.username || entry.playerId}</div>
                    <div className="text-xs text-slate-500">{entry.lastLapTimeMs ? `Last lap ${formatLapTime(entry.lastLapTimeMs)}` : 'Last lap --:--.--'}</div>
                  </div>
                </div>
                <div className="text-right text-sm text-slate-300">
                  <div>Lap {Math.min(entry.lap || 0, track.lapsToWin || state.config?.lapsToWin || 1)}</div>
                  <div>{formatSpeed(entry.speed, track.maxSpeed || 0.18)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

SlotCarsGame.Guide = function SlotCarsGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">SlotCars</h3>
      <p>Hold the throttle to accelerate. Straights are safe at full speed. Corners have a safe speed if you want to stay on the track.</p>
    </div>
  );
};

export default SlotCarsGame;